import React, { useCallback, useMemo, useState } from "react";
import {
  LogOut,
  Monitor,
  Moon,
  Palette,
  SquareTerminal,
  Sun,
  Trash2,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useSystem } from "../system";
import { useViewMode } from "../../ViewMode";
import { useWindows, zoneRect } from "../WindowManager";
import { DESKTOP_DIR, RECYCLE_BIN, THIS_PC, useFsVersion } from "../fs";
import { NodeIcon } from "../fileIcons";
import { useMenu } from "./ContextMenu";
import {
  backgroundMenu,
  itemMenu,
  recycleBinMenu,
  thisPcMenu,
} from "../menus";
import Window from "./Window";
import "./Desktop.css";

const SORTERS = {
  name: (a, b) => a.label.localeCompare(b.label),
  type: (a, b) => (a.kind || "").localeCompare(b.kind || ""),
  size: (a, b) => (a.node?.size || "").localeCompare(b.node?.size || ""),
  modified: (a, b) => (b.node?.modified || "").localeCompare(a.node?.modified || ""),
};

export default function Desktop({ mobile, surfaceRef }) {
  const { apps, windows, activeId, open, surface } = useWindows();
  const { isDark, toggleTheme } = useTheme();
  const { wallpaper } = useSystem();
  const { exitWindows } = useViewMode();
  const { showMenu } = useMenu();
  const fsVersion = useFsVersion();

  const [selected, setSelected] = useState(null);
  const [snapZone, setSnapZone] = useState(null);
  const [iconSize, setIconSize] = useState("large");
  const [sortBy, setSortBy] = useState("name");
  const [showIcons, setShowIcons] = useState(true);
  const [spin, setSpin] = useState(0); // bumped by Refresh

  const openProperties = useCallback(
    (nodeId) => open("properties", { nodeId }),
    [open]
  );

  const openSystemProperties = useCallback(
    () => open("properties", { system: true }),
    [open]
  );

  /** Desktop icons: system shells first, then the Desktop folder's contents. */
  const items = useMemo(() => {
    const edge = apps.find((a) => a.id === "edge");
    const list = [
      {
        key: "this-pc",
        label: "This PC",
        kind: "System Folder",
        render: <Monitor size={30} strokeWidth={1.6} />,
        tone: "#4a90d9",
        tile: true,
        onOpen: () => open("explorer", { folderId: THIS_PC.id }),
        menu: () => thisPcMenu({ open, openSystemProperties }),
      },
      {
        key: "recycle-bin",
        label: "Recycle Bin",
        kind: "System Folder",
        render: <Trash2 size={28} strokeWidth={1.6} />,
        tone: "#6b7d8f",
        tile: true,
        badge: RECYCLE_BIN.children.length,
        onOpen: () => open("explorer", { folderId: RECYCLE_BIN.id }),
        menu: () =>
          recycleBinMenu({
            open,
            count: RECYCLE_BIN.children.length,
            openProperties,
          }),
      },
    ];

    if (edge) {
      list.push({
        key: "edge",
        label: edge.title,
        kind: "Application",
        render: <edge.icon size={28} strokeWidth={1.7} />,
        tone: edge.color,
        tile: true,
        onOpen: () => open("edge"),
        menu: () => [
          { label: "Open", icon: edge.icon, onClick: () => open("edge") },
          { label: "New window", icon: edge.icon, onClick: () => open("edge") },
        ],
      });
    }

    (DESKTOP_DIR.children || []).forEach((node) => {
      list.push({
        key: node.id,
        label: node.name,
        kind: node.kind,
        node,
        render: <NodeIcon node={node} size={34} />,
        shortcut: node.shortcut,
        onOpen: () =>
          node.type === "file"
            ? node.open?.app &&
              open(node.open.app, { fileId: node.id, ...node.open })
            : open("explorer", { folderId: node.target || node.id }),
        menu: () => itemMenu({ node, open, openProperties, apps }),
      });
    });

    const [systemIcons, fileIcons] = [list.slice(0, 3), list.slice(3)];
    fileIcons.sort(SORTERS[sortBy] || SORTERS.name);
    return [...systemIcons, ...fileIcons];
    // fsVersion re-reads the Desktop folder after a new/delete/restore
    // eslint-disable-next-line
  }, [apps, open, openProperties, openSystemProperties, sortBy, fsVersion]);

  const backgroundItems = () =>
    backgroundMenu({
      folderId: DESKTOP_DIR.id,
      view: iconSize,
      setView: setIconSize,
      sort: sortBy,
      setSort: setSortBy,
      onRefresh: () => setSpin((n) => n + 1),
      extra: [
        {
          label: "Show desktop icons",
          checked: showIcons,
          onClick: () => setShowIcons((v) => !v),
        },
        {
          label: "Open in Terminal",
          icon: SquareTerminal,
          onClick: () => open("terminal"),
        },
      ],
      tail: [
        {
          label: `Switch to ${isDark ? "light" : "dark"} mode`,
          icon: isDark ? Sun : Moon,
          onClick: toggleTheme,
        },
        {
          label: "Display settings",
          icon: Monitor,
          onClick: () => open("settings"),
        },
        {
          label: "Personalise",
          icon: Palette,
          onClick: () => open("settings"),
        },
        {
          label: "Back to the classic site",
          icon: LogOut,
          onClick: exitWindows,
        },
      ],
    });

  const preview = snapZone ? zoneRect(snapZone, surface.w, surface.h) : null;

  return (
    <div
      className="desktop"
      ref={surfaceRef}
      onPointerDown={(e) => e.target === e.currentTarget && setSelected(null)}
      onContextMenu={(e) => {
        if (e.target !== e.currentTarget) return;
        showMenu(e, backgroundItems());
      }}
    >
      <div
        className="desktop__wallpaper"
        aria-hidden="true"
        style={{ background: isDark ? wallpaper.dark : wallpaper.light }}
      >
        {wallpaper.petals && (
          <>
            <span className="bloom bloom--1" />
            <span className="bloom bloom--2" />
            <span className="bloom bloom--3" />
            <span className="bloom bloom--4" />
          </>
        )}
      </div>

      {showIcons && (
        <div
          className={`desk-icons desk-icons--${iconSize}`}
          key={spin}
          data-refresh={spin}
        >
          {items.map((item) => (
            <button
              key={item.key}
              className={`desk-icon ${
                selected === item.key ? "desk-icon--selected" : ""
              }`}
              onClick={() => (mobile ? item.onOpen() : setSelected(item.key))}
              onDoubleClick={item.onOpen}
              onContextMenu={(e) => {
                setSelected(item.key);
                showMenu(e, item.menu());
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  item.onOpen();
                }
              }}
              aria-label={`Open ${item.label}`}
            >
              <span
                className={`desk-icon__art ${
                  item.tile ? "desk-icon__art--tile" : ""
                }`}
                style={{ "--tile": item.tone }}
              >
                {item.render}
                {item.shortcut && (
                  <span className="desk-icon__link" aria-hidden="true" />
                )}
              </span>
              <span className="desk-icon__label">{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="snap-preview"
          style={{
            left: preview.x,
            top: preview.y,
            width: preview.w,
            height: preview.h,
          }}
        />
      )}

      {windows.map((win) => (
        <Window
          key={win.id}
          win={win}
          app={apps.find((a) => a.id === win.appId)}
          active={activeId === win.id}
          mobile={mobile}
          onSnapPreview={setSnapZone}
        />
      ))}
    </div>
  );
}
